import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BlogCard } from "@/components/BlogCard";
import { Plus, Search, PenTool } from "lucide-react";
import { fetchAllBlogs, deleteBlog } from "@/lib/api"; // Import deleteBlog
import { BlogPost } from "@/types/blog";
import { useToast } from "@/hooks/use-toast"; // Import useToast

const Index = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getBlogs = async () => {
    try {
      const data = await fetchAllBlogs();
      // Ensure data is always an array
      setBlogs(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error in getBlogs:', err);
      setError(err.message);
      setBlogs([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBlogs();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await deleteBlog(id);
      toast({
        title: "Blog Deleted",
        description: "The blog post has been successfully deleted.",
      });
      getBlogs(); // Re-fetch blogs after deletion
    } catch (err: any) {
      toast({
        title: "Error",
        description: `Failed to delete blog: ${err.message}`,
        variant: "destructive",
      });
      setError(err.message);
    }
  };
  
  const filteredBlogs = Array.isArray(blogs) ? blogs.filter(blog =>
    blog?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog?.excerpt?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-xl text-foreground">Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-blog-hero text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-fade-in">
            <PenTool className="mx-auto mb-6 h-16 w-16" />
            <h1 className="mb-6 text-5xl font-bold">Modern Blog Platform</h1>
            <p className="mb-8 text-xl opacity-90 max-w-2xl mx-auto">
              Discover insightful articles, share your thoughts, and connect with a community of writers and readers.
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate('/create')}
              className="shadow-lg hover:shadow-xl transition-shadow"
            >
              <Plus className="mr-2 h-5 w-5" />
              Write Your First Post
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Search and Actions */}
          <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => navigate('/create')} className="bg-blog-hero hover:opacity-90">
              <Plus className="mr-2 h-4 w-4" />
              Create New Post
            </Button>
          </div>

          {error && (
            <div className="text-center py-16">
              <h3 className="text-2xl font-semibold mb-4 text-red-500">Error: {error}</h3>
              <p className="text-muted-foreground mb-6">
                There was an error fetching the blog posts. Please make sure your backend server is running.
              </p>
            </div>
          )}

          {/* Blog Grid */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredBlogs.map((post, index) => (
              <div
                key={post.id}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <BlogCard
                  post={post}
                  onClick={() => navigate(`/blog/${post.id}`)}
                  onDelete={handleDelete} // Pass handleDelete to BlogCard
                />
              </div>
            ))}
          </div>

          {filteredBlogs.length === 0 && !error && (
            <div className="text-center py-16">
              <h3 className="text-2xl font-semibold mb-4">No articles found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search terms or create a new blog post.
              </p>
              <Button onClick={() => navigate('/create')} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Create First Post
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Index;
